import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import {
  addAnswer,
  clearAnsweres,
  clearEditAnswer,
} from "../../../../redux/survey/actions";
import GridSpotImage from "../../../../Survey/common/components/GridSpotImage";
import {
  isGridspotMultiSelect,
  readGridspotSelectedOptionIds,
  toGridspotAnswerValue,
  toggleGridspotAnswerValue,
} from "../../../../Survey/helpers/gridspot";
import { filterVisibleSurveyQuestions } from "../../../../Survey/helpers/surveyHelper";

const GridSpotType = (props) => {
  const intl = useIntl();
  const {
    finishSurvey,
    question,
    answeres,
    addAnswer_,
    clearAnsweres_,
    onSend,
    allQuestionsOriginal,
    setAllQuestions,
    surveyType,
    currentQuestionIndex,
    allQuestions,
    edit_answer,
    survey_theme,
    clearEditAnswer_,
    hideQuestion,
  } = props;

  const editingQuestionId =
    edit_answer && Object.keys(edit_answer).length
      ? allQuestions[edit_answer.question]?._id
      : null;
  const isEditingThisQuestion = Boolean(
    editingQuestionId && String(editingQuestionId) === String(question._id),
  );
  const isEditingAnotherQuestion = Boolean(
    editingQuestionId && String(editingQuestionId) !== String(question._id),
  );
  const isMultiSelect = isGridspotMultiSelect(question);
  const isLastQuestion = currentQuestionIndex + 1 >= allQuestions?.length;
  const selectingRef = useRef(false);
  const lastPointRef = useRef(null);

  // Multi-select keeps taps in local state until Continue. Writing each tap to
  // Redux triggers mobile chat history rebuild and auto-advances the question.
  const [localSelectedIds, setLocalSelectedIds] = useState(() =>
    readGridspotSelectedOptionIds(answeres[question._id]?.value),
  );

  useEffect(() => {
    selectingRef.current = false;
    // Restore any previously saved tap point so edit→Continue without a new
    // tap does not clear gridspot_point.
    lastPointRef.current = answeres[question._id]?.gridspot_point ?? null;
    setLocalSelectedIds(
      readGridspotSelectedOptionIds(answeres[question._id]?.value),
    );
  }, [question?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEditingThisQuestion) return;
    lastPointRef.current = answeres[question._id]?.gridspot_point ?? null;
    setLocalSelectedIds(
      readGridspotSelectedOptionIds(answeres[question._id]?.value),
    );
  }, [isEditingThisQuestion, question?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedIds = isMultiSelect
    ? localSelectedIds
    : readGridspotSelectedOptionIds(answeres[question._id]?.value);
  const hasSelection = selectedIds.length > 0;

  const beginAction = () => {
    if (selectingRef.current) return false;
    selectingRef.current = true;
    return true;
  };

  const releaseAction = () => {
    selectingRef.current = false;
  };

  const restoreSavedMultiSelection = () => {
    lastPointRef.current = answeres[question._id]?.gridspot_point ?? null;
    setLocalSelectedIds(
      readGridspotSelectedOptionIds(answeres[question._id]?.value),
    );
  };

  const applyTabletJourneyFilter = (value) => {
    if (
      isEditingThisQuestion ||
      surveyType !== "tablet" ||
      currentQuestionIndex !== 0
    ) {
      return;
    }
    clearAnsweres_();
    const percentage = (Number(value) / Number(question?.maxScore)) * 100;
    const filteredQuestion = filterVisibleSurveyQuestions(
      allQuestionsOriginal
        .filter((item) => item._id !== question._id)
        .filter((item) =>
          percentage >= 70
            ? item.journey_type === "neutral" ||
              item.journey_type === "positive"
            : item.journey_type === "neutral" ||
              item.journey_type === "negative",
        ),
    );
    setAllQuestions([question, ...filteredQuestion]);
  };

  const cancelEdit = () => {
    if (!isEditingThisQuestion) return;
    // Drop unsaved toggles so Cancel does not leave a dirty highlight / draft.
    restoreSavedMultiSelection();
    releaseAction();
    clearEditAnswer_();
  };

  const leaveWithoutSelection = (actionAlreadyStarted = false) => {
    if (isEditingThisQuestion) {
      cancelEdit();
      return;
    }
    if (isEditingAnotherQuestion || question?.required) return;
    if (!actionAlreadyStarted && !beginAction()) return;
    onSend("");
    if (isLastQuestion) {
      finishSurvey(true, question);
    }
  };

  const selectedLabels = (ids = selectedIds) =>
    ids
      .map(
        (id) =>
          question.options?.find((opt) => String(opt._id) === String(id))
            ?.text || "",
      )
      .filter(Boolean)
      .join("$#&");

  /** Sum of selected option scores — same journey input as checkbox multiselect. */
  const selectedOptionsScore = (ids = selectedIds) =>
    (question.options || []).reduce((sum, option) => {
      if (!ids.some((id) => String(id) === String(option._id))) return sum;
      return sum + Number(option.value || 0);
    }, 0);

  /** Union hide_questions from selected spots — same pattern as CheckBoxType. */
  const collectHideQuestions = (ids) =>
    (question.options || [])
      .filter((opt) => ids.some((id) => String(id) === String(opt._id)))
      .flatMap((opt) => opt?.hide_questions || []);

  const commitMultiSelection = (ids) => {
    try {
      applyTabletJourneyFilter(selectedOptionsScore(ids));

      const point =
        lastPointRef.current ?? answeres[question._id]?.gridspot_point ?? null;
      const payload = {
        id: question._id,
        value: ids,
        type: "id",
      };
      if (ids.length && point != null) {
        payload.gridspot_point = point;
      } else if (!ids.length) {
        // Clear stale tap coords when the answer is emptied on edit.
        payload.gridspot_point = null;
      }
      addAnswer_(payload);

      const isFinishSurvey = !isEditingThisQuestion && isLastQuestion;
      // Same order as CheckBoxType: advance chat, apply hide (even on last),
      // then finish — so hide_questions still filter before submit.
      onSend(selectedLabels(ids));
      if (hideQuestion) {
        hideQuestion(collectHideQuestions(ids), question._id, ids);
      }
      if (isFinishSurvey) {
        finishSurvey(true, question);
      }
    } finally {
      releaseAction();
    }
  };

  const continueWithSelection = () => {
    if (question?.required && !hasSelection) return;
    if (!beginAction()) return;

    try {
      if (!hasSelection) {
        // Non-required multi-select edit: persist empty so the prior answer is cleared.
        if (isEditingThisQuestion && isMultiSelect && !question?.required) {
          commitMultiSelection([]);
          return;
        }
        leaveWithoutSelection(true);
        return;
      }

      if (isMultiSelect) {
        commitMultiSelection(selectedIds);
        return;
      }

      if (selectedIds[0]) {
        const option = question.options?.find(
          (opt) => String(opt._id) === String(selectedIds[0]),
        );
        if (option) applyTabletJourneyFilter(option.value);
      }

      if (!isEditingThisQuestion && isLastQuestion) {
        finishSurvey(true, question);
      }
      onSend(selectedLabels());
    } finally {
      releaseAction();
    }
  };

  const selectOption = (option, point) => {
    if (isEditingAnotherQuestion) return;

    if (isMultiSelect) {
      lastPointRef.current = point;
      setLocalSelectedIds((prev) =>
        toggleGridspotAnswerValue(prev, option._id),
      );
      return;
    }

    if (!beginAction()) return;
    applyTabletJourneyFilter(option.value);
    addAnswer_({
      id: question._id,
      value: toGridspotAnswerValue(option._id),
      type: "id",
      // Where the respondent actually tapped (see Survey/pages/Questions/GridSpot).
      gridspot_point: point,
    });

    const isFinishSurvey = !isEditingThisQuestion && isLastQuestion;
    const selectedValue = toGridspotAnswerValue(option._id);
    // Same order as CheckBoxType: advance chat, apply hide (even on last),
    // then finish — so hide_questions still filter before submit.
    onSend(option.text || "");
    if (hideQuestion) {
      hideQuestion(option?.hide_questions, question._id, selectedValue);
    }
    if (isFinishSurvey) {
      finishSurvey(true, question);
    }
  };

  const canSubmitWithoutTap = !question?.required;
  const canSubmitLast = canSubmitWithoutTap || hasSelection;
  const showContinue =
    isMultiSelect &&
    !isEditingAnotherQuestion &&
    (hasSelection || isEditingThisQuestion);
  const showLastSubmit =
    isLastQuestion &&
    !isEditingThisQuestion &&
    !isEditingAnotherQuestion &&
    (!isMultiSelect || !hasSelection);
  const showSkip =
    !question?.required &&
    !isLastQuestion &&
    !isEditingThisQuestion &&
    !isEditingAnotherQuestion &&
    !(isMultiSelect && hasSelection);

  return (
    <div className="px-3 pb-4">
      <GridSpotImage
        question={question}
        selectedOptionIds={selectedIds}
        disabled={isEditingAnotherQuestion}
        onSelect={selectOption}
        accentColor={survey_theme?.primaryColor}
        alt={
          question?.text
            ? String(question.text).replace(/<[^>]+>/g, "")
            : "Survey image"
        }
      />
      {isEditingThisQuestion ? (
        <div className="flex justify-end mt-3 space-x-3">
          <button
            type="button"
            onClick={cancelEdit}
            className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium leading-4 transition-all duration-200 bg-transparent border rounded-full shadow-sm"
            style={{
              color: survey_theme?.primaryColor,
              borderColor: survey_theme?.primaryColor || "#7c3aed",
            }}
          >
            {intl.formatMessage({ id: "cancel", defaultMessage: "Cancel" })}
          </button>
          {isMultiSelect ? (
            <button
              type="button"
              onClick={continueWithSelection}
              disabled={question?.required && !hasSelection}
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium leading-4 transition-all duration-200 border rounded-full shadow-sm"
              style={{
                backgroundColor:
                  question?.required && !hasSelection
                    ? "#9CA3AF"
                    : survey_theme?.primaryColor,
                borderColor:
                  question?.required && !hasSelection
                    ? "#9CA3AF"
                    : survey_theme?.primaryColor || "#7c3aed",
                color: "#fff",
              }}
            >
              {intl.formatMessage({ id: "continue", defaultMessage: "Continue" })}
            </button>
          ) : null}
        </div>
      ) : null}
      {showContinue && !isEditingThisQuestion ? (
        <div className="flex justify-end mt-3">
          <button
            type="button"
            onClick={continueWithSelection}
            className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium leading-4 transition-all duration-200 border rounded-full shadow-sm"
            style={{
              backgroundColor: survey_theme?.primaryColor,
              borderColor: survey_theme?.primaryColor || "#7c3aed",
              color: "#fff",
            }}
          >
            {isLastQuestion
              ? intl.formatMessage({ id: "submit" })
              : intl.formatMessage({
                  id: "continue",
                  defaultMessage: "Continue",
                })}
          </button>
        </div>
      ) : null}
      {showSkip ? (
        <div className="flex justify-end mt-3">
          <button
            type="button"
            onClick={() => leaveWithoutSelection()}
            className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium leading-4 transition-all duration-200 bg-transparent border rounded-full shadow-sm"
            style={{
              color: survey_theme?.primaryColor,
              borderColor: survey_theme?.primaryColor || "#7c3aed",
            }}
          >
            {intl.formatMessage({ id: "skip" })}
          </button>
        </div>
      ) : null}
      {showLastSubmit ? (
        <div className="fixed bottom-0 z-10 w-full max-w-lg mx-auto -translate-x-1/2 left-1/2 answerContainer">
          <div className="px-2 bg-white border-t border-gray-200">
            <div className="flex items-center justify-end space-x-3 h-18">
              <button
                type="button"
                className="btn icon-btn icon-right"
                title={intl.formatMessage({ id: "submit" })}
                style={{
                  backgroundColor: canSubmitLast
                    ? survey_theme?.primaryColor
                    : "#9CA3AF",
                  border: "1px solid",
                  color: "#fff",
                  borderRadius: "1rem",
                }}
                disabled={!canSubmitLast}
                onClick={() => {
                  if (question?.required && !hasSelection) return;
                  if (!beginAction()) return;
                  try {
                    if (hasSelection) {
                      if (isMultiSelect) {
                        commitMultiSelection(selectedIds);
                        return;
                      }
                      // Radio last-question submit: same hide-then-finish as selectOption.
                      onSend(selectedLabels());
                      if (hideQuestion) {
                        const selectedOption = question.options?.find(
                          (opt) =>
                            String(opt._id) === String(selectedIds[0]),
                        );
                        hideQuestion(
                          selectedOption?.hide_questions,
                          question._id,
                          toGridspotAnswerValue(selectedIds[0]),
                        );
                      }
                      finishSurvey(true, question);
                      return;
                    }
                    leaveWithoutSelection(true);
                  } finally {
                    releaseAction();
                  }
                }}
              >
                {intl.formatMessage({ id: "submit" })}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = (state) => ({
  answeres: state.survey.answeres,
  edit_answer: state.survey.edit_answer,
});

const mapDispatchToProps = (dispatch) => ({
  addAnswer_: (data) => dispatch(addAnswer(data)),
  clearAnsweres_: () => dispatch(clearAnsweres()),
  clearEditAnswer_: () => dispatch(clearEditAnswer()),
});

export default connect(mapStateToProps, mapDispatchToProps)(GridSpotType);
